import "@/components/Layout/Table/TableSortButton/TableSortButton.scss";

import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { FCC } from "types";

import { ArrowRight } from "@/assets/icons/icons";

interface Props {
  title: string;
  sortField: string;
}

export const TableSortButton: FCC<Props> = ({ title, sortField }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentlyActiveSortField = searchParams.get("sortField");
  const sortFieldIsActive = currentlyActiveSortField === sortField;
  const currentlyActiveSortDirection = searchParams.get("sortDirection");

  const setSortField = (sortField: string) => {
    const updatedSearchParams = new URLSearchParams(searchParams.toString());

    // Re-sorting reorders the whole list, so the old page number points at an
    // arbitrary mid-list slice of the new order. Back to page 1 — dropping the
    // param is how the tables express that (`clampPage` reads a missing `page`
    // as 1).
    updatedSearchParams.delete("page");

    if (sortFieldIsActive) {
      if (currentlyActiveSortDirection === "asc") {
        updatedSearchParams.set("sortDirection", "desc");
      }
      if (currentlyActiveSortDirection === "desc") {
        updatedSearchParams.delete("sortField");
        updatedSearchParams.delete("sortDirection");
      }
    } else {
      updatedSearchParams.set("sortField", sortField);
      updatedSearchParams.set("sortDirection", "asc");
    }
    setSearchParams(updatedSearchParams.toString());
  };

  return (
    <button
      aria-label={t("layout.table.sortBy", { title })}
      title={t("layout.table.sortBy", { title })}
      className="sortable"
      onClick={() => setSortField(sortField)}
    >
      {/* min-w-0 lets a long single-word header break instead of overflowing its
          column. Where the break should land — and whether it shows a hyphen —
          is not something CSS can express for a compound noun, so the German
          "Rechnungsnummer" carries a soft hyphen (`U+00AD`) at the compound
          boundary (see locales/de): invisible while the word fits on one line,
          rendered as "-" only where the browser actually breaks it. Otherwise
          the column width alone decides and it splits mid-syllable as
          "Rechnungsnu / mmer" with no hyphen at all. */}
      <span className="min-w-0 break-words">{title}</span>
      <ArrowRight
        className={`arrow ${
          sortFieldIsActive
            ? `${
                currentlyActiveSortDirection === "desc"
                  ? "rotate-90"
                  : "-rotate-90"
              }`
            : "hidden"
        }`}
      />
    </button>
  );
};
